<template>
    <div
        ref="element"
        :class="{
            'bg-dark-lighter': hasFocus,
            'cursor-not-allowed opacity-60': disabled,
        }"
        class="scrollableContent bg-dark-light flex w-full items-center rounded-lg p-3"
    >
        <div class="flex grow flex-col">
            <div class="text-xxs font-bold tracking-widest uppercase" @click="!disabled && $refs.select.focus()">
                <span> {{ label }} <BadgePremium v-if="premium" class="normal-case" /> </span>
                <span v-if="formatDisplayedValue.unit"> ({{ formatDisplayedValue.unit }})</span>
                <RequiredText v-if="required"> *</RequiredText>
            </div>
            <Blur class="flex flex-col" :blurred="!$store.getters['auth/hasSubscriptionAccess'] && premium">
                <select
                    ref="select"
                    class="h-6 w-full overflow-y-hidden border-none bg-transparent p-0 pr-8 text-ellipsis text-white outline-hidden focus:ring-0 focus:outline-hidden disabled:cursor-not-allowed"
                    :value="formatDisplayedValue.value"
                    :disabled="disabled"
                    @input="formatRealValue($event.target.value)"
                    @focus="hasFocus = true"
                    @blur="hasFocus = false"
                >
                    <option v-if="!emptyOptionLabel" class="text-gray-500" value="" disabled selected hidden>
                        {{ $t('form.select-option') }}
                    </option>
                    <option v-else value="">
                        {{ emptyOptionLabel }}
                    </option>
                    <slot />
                </select>
            </Blur>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { convertValueUnit, getUserUnit } from '@/lib/helpers/convert'
import { isEmptyOptionValue } from '@/lib/helpers/numeric'
import RequiredText from '@/components/form/RequiredText.vue'
import BadgePremium from '@/components/BadgePremium.vue'
import Blur from '@/components/Blur.vue'

const props = defineProps({
    modelValue: { type: [String, Number, Date, null], default: '' },
    decimals: { type: Number, default: undefined },
    unit: { type: String, default: null },
    userUnit: { type: String, default: null },
    label: { type: String, default: '' },
    required: { type: Boolean, default: false },
    premium: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    emptyOptionLabel: { type: String, default: '' },
    emptyOptionValue: { default: '' },
})
const emits = defineEmits(['update:modelValue'])

const hasFocus = ref(false)
const select = ref(undefined)
const element = ref(undefined)

const userUnit = computed(() => (props.unit ? getUserUnit(props.unit, props.userUnit) : null))

const formatDisplayedValue = computed(() => {
    const convertedValue = convertValueUnit(props.modelValue, props.unit, userUnit.value, props.decimals, true)
    return isEmptyOptionValue(convertedValue.value, props) ? { ...convertedValue, value: '' } : convertedValue
})

const formatRealValue = async (value) =>
    emits(
        'update:modelValue',
        convertValueUnit(
            props.emptyOptionLabel && value === '' ? props.emptyOptionValue : value,
            userUnit.value,
            props.unit,
            props.decimals,
            true
        ).value
    )
</script>
